import type { Maybe } from '@/types/globals';
import { Inject, Injectable } from '@angular/core';
import { DentureDetailsUtils } from '@modules/denture-details/utils/denture-details-utils';
import { OrderQuery } from '@modules/order/state/order-query';
import { ProcedureMap } from '@shared/models/procedure-map';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { IS_NOTES_VISIBLE$ } from '@shared/services/sections-visibility.tokens';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { isLegacyRestorative, isResorative } from '@shared/utils/restorative-check-util';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class RxForModelingFacade {
	isProcedureFlow$: Observable<boolean> = this.shellQuery.isProcedureFlow$;
	rx$ = this.shellQuery.rx$;
	isNotesVisible$: Observable<boolean> = this.isNotesVisibleToken$;
	syncModelingReadOnly$ = this.rxTemplateService.template$.pipe(
		map(template => template.ui.modeling.readOnly),
		distinctUntilChanged(),
		tap(isReadOnly => {
			if (isReadOnly) {
				this.shellStore.update({ isReadOnly });
			}
		})
	);
	procedureId$: Observable<Maybe<number>> = this.shellQuery.rx$.pipe(map(rx => rx?.Order?.ProcedureId));
	isDentureDetailsVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => !!procedureMap?.ProcedureId && DentureDetailsUtils.isDentureDetailsEnabled(procedureMap.ProcedureId))
	);
	isProcedureResorative$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		withLatestFrom(this.shellQuery.isProcedureFlow$),
		map(
			([procedureMap, isProcedureFlow]: [ProcedureMap, boolean]) =>
				isProcedureFlow && this.isResorativeOrLegacyRestorative({ caseTypeId: procedureMap?.CaseTypeId })
		)
		// TODO: isProcedureFlow && procedureMap?.ToothDiagramIsEnabled when ITEROBIZ-60147 is implemented
	);

	isCaseFlowRestorative$: Observable<boolean> = this.shellQuery.rx$.pipe(
		map(rx => !!rx?.Order?.CaseTypeId && this.isResorativeOrLegacyRestorative({ caseTypeId: rx?.Order?.CaseTypeId }))
	);

	isRestoOrder$: Observable<boolean> = combineLatest([this.isCaseFlowRestorative$, this.isProcedureResorative$]).pipe(
		map(([isCaseFlowRestorative, isProcedureResorative]) => isProcedureResorative || isCaseFlowRestorative)
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly shellStore: ShellStore,
		private readonly orderQuery: OrderQuery,
		private readonly rxTemplateService: RxTemplateService,
		@Inject(IS_NOTES_VISIBLE$) private readonly isNotesVisibleToken$: Observable<boolean>
	) {}

	private isResorativeOrLegacyRestorative({ caseTypeId }: { caseTypeId: number }): boolean {
		const isResto = isResorative({ caseTypeId });
		const isLegacyResto = isLegacyRestorative({ caseTypeId });

		return isResto || isLegacyResto;
	}
}
